import { api } from '@/lib/axios'

/**
 * AP-35 Phase E — Anschluss der Zeiterfassung und Übernahme ihrer Aufträge
 * (Detailblatt 4 6.1, 6.6).
 */

export interface TimeBridgeState {
  connected: boolean
  projectSource: string
  projectTimeBooked: boolean
  counts: { unlinkedRefs: number; unlinkedActiveRefs: number; ignoredRefs: number; projectsToMirror: number }
}

export interface MirrorResult {
  mirrored: number
  failed: { projectId: string; projectNumber: string; code: string }[]
}

export interface UnlinkedRef {
  id: string
  projectNumber: string
  name: string
  source: string
  isActive: boolean
  ignored: boolean
  positions: number
  steps: number
  entries: number
  lastBookedOn: string | null
  collision: { projectId: string; projectNumber: string; name: string } | null
}

export interface ImportResult {
  dryRun: boolean
  toImport: { refId: string; projectNumber: string; name: string }[]
  skipped: { refId: string; projectNumber: string; name: string; collision: { projectNumber: string; name: string } }[]
  renumberedSteps: number
  imported: { refId: string; projectId: string; projectNumber: string }[]
  failed: { refId: string; projectNumber: string; code: string }[]
}

export async function fetchTimeBridge(): Promise<TimeBridgeState> {
  return (await api.get<{ data: TimeBridgeState }>('/projects/time-bridge')).data.data
}

export async function connectTimeBridge(): Promise<{ connected: boolean; deactivatedRefs: number; mirror: MirrorResult }> {
  return (await api.post('/projects/time-bridge/connect')).data.data
}

export async function disconnectTimeBridge(): Promise<TimeBridgeState> {
  return (await api.post<{ data: TimeBridgeState }>('/projects/time-bridge/disconnect')).data.data
}

export async function rebuildMirror(): Promise<MirrorResult> {
  return (await api.post<{ data: MirrorResult }>('/projects/time-bridge/rebuild')).data.data
}

export async function fetchUnlinkedRefs(includeIgnored: boolean): Promise<UnlinkedRef[]> {
  return (await api.get<{ data: UnlinkedRef[] }>('/projects/time-bridge/unlinked', { params: includeIgnored ? { includeIgnored: true } : {} })).data.data
}

export async function importRefs(refIds: string[], numbering: 'KEEP' | 'NEW', dryRun: boolean): Promise<ImportResult> {
  return (await api.post<{ data: ImportResult }>('/projects/time-bridge/import', { refIds, numbering, dryRun })).data.data
}

export async function importRefWithNewNumber(refId: string): Promise<ImportResult> {
  return (await api.post<{ data: ImportResult }>(`/projects/time-bridge/unlinked/${refId}/import-new-number`)).data.data
}

export async function linkRef(refId: string, projectId: string): Promise<void> {
  await api.post(`/projects/time-bridge/unlinked/${refId}/link`, { projectId })
}

export async function ignoreRef(refId: string, ignored: boolean): Promise<void> {
  await api.post(`/projects/time-bridge/unlinked/${refId}/ignore`, { ignored })
}

export async function searchProjectsForLink(search: string): Promise<{ id: string; projectNumber: string; name: string }[]> {
  const res = await api.get<{ data: { id: string; projectNumber: string; name: string }[] }>('/projects', { params: { search, limit: 20 } })
  return res.data.data
}
